import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { ConfigProvider, theme } from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";
import PanesContainer from "../main/components/PanesContainer";
import { differenceWith } from "lodash-es";
import { matchRoutes, useLocation, useNavigate } from "react-router-dom";
import { useMemoizedFn } from "ahooks";
import { initTabEvent } from "./utils/initTabEvent";
import { routesArraySelector, useAppStore } from "../store";
import { ProConfigProvider, ProLayout } from "@ant-design/pro-components";
import { ThemeProvider } from "styled-components";
import SingleOutlet from "./components/SingleOutlet";
import { getGlobalShareConfigValueByKey } from "../utils/globalShareConfig";
import { GlobalStyle } from "../theme/global";
import { useHistoryListener } from "./useHistoryListener";
import { useProLayoutProps } from "../main/hooks/useProLayoutProps";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var SubLayout = function SubLayout(props) {
  var _Object$keys, _props$proLayoutConfi;
  var _useContext = useContext(ConfigProvider.ConfigContext),
    getPrefixCls = _useContext.getPrefixCls;
  var themeToken = theme.useToken().token;
  var isTabsMode = getGlobalShareConfigValueByKey("tabsMode");
  var prefixCls = getPrefixCls();
  var subAppRef = useRef(null);
  // 菜单收缩状态
  var appData = props.appData,
    layoutConfig = props.layoutConfig;
  var clientRoutes = appData.clientRoutes;
  var _useState = useState(isTabsMode),
    _useState2 = _slicedToArray(_useState, 2),
    keepAlive = _useState2[0],
    setKeepAlive = _useState2[1];
  var panes = useAppStore(function (s) {
    return s.panes;
  });
  var setFlatPathRoutes = useAppStore(function (s) {
    return s.setFlatPathRoutes;
  });
  var _useAppStore = useAppStore(function (s) {
      return s.tabCacheHandlers;
    }),
    cleanOtherCache = _useAppStore.cleanOtherCache;
  var setPathname = useAppStore(function (s) {
    return s.setPathname;
  });
  var setPanes = useAppStore(function (s) {
    return s.setPanes;
  });
  var setRefreshPath = useAppStore(function (s) {
    return s.setRefreshPath;
  });
  var flatPathRoutes = useAppStore(function (s) {
    return s.flatPathRoutes;
  });
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    initSentryConfig = _useState4[0],
    setInitSentryConfig = _useState4[1];
  var monitorConfig = useAppStore(function (s) {
    return s.monitorConfig;
  });
  var baseRoute = useRef("");
  var routesPaths = useAppStore(routesArraySelector);
  var _useLocation = useLocation(),
    pathname = _useLocation.pathname;
  var setErrorCaptureException = useAppStore(function (s) {
    return s.setErrorCaptureException;
  });
  var navigate = useNavigate();
  var routes = appData.routes;
  useHistoryListener(baseRoute.current);
  var removeTabs = useMemoizedFn(function (pathArray) {
    var newPanes = differenceWith(panes, pathArray, function (pane, path) {
      return pane.path === path;
    });
    setPanes(_toConsumableArray(newPanes));
    cleanOtherCache(newPanes.map(function (el) {
      return el.path;
    }));
  });
  var addTab = useMemoizedFn(function (_ref) {
    var path = _ref.path,
      search = _ref.search;
    var $panes = _toConsumableArray(panes);
    var _ref2 = matchRoutes(routesPaths, path) || [],
      _ref3 = _slicedToArray(_ref2, 1),
      matchRoute = _ref3[0];
    var dynamicPath = matchRoute === null || matchRoute === void 0 ? void 0 : matchRoute.route.path;
    var existingPane = $panes.find(function (c) {
      return c.path === path;
    });
    var willAddPaneKey = Object.keys(routes).find(function (key) {
      return routes[key].path === dynamicPath;
    });
    // 可能为404
    var willAddPane = willAddPaneKey ? _objectSpread({}, routes[willAddPaneKey]) : {
      id: "@@404",
      path: path,
      name: "PageNotFound",
      keepAlive: false
    };
    if (existingPane) {
      existingPane.search = search;
      setPanes($panes);
      return;
    }
    willAddPane.search = search;
    willAddPane.dynamicPath = dynamicPath;
    willAddPane.path = path;
    setPanes([].concat(_toConsumableArray(panes), [willAddPane]));
  });
  var checkPathname = function checkPathname(props) {
    var _props$extra;
    var $pathname = (props === null || props === void 0 ? void 0 : props.pathname) || pathname;
    var $search = props === null || props === void 0 ? void 0 : props.search;
    baseRoute.current = (props === null || props === void 0 || (_props$extra = props.extra) === null || _props$extra === void 0 ? void 0 : _props$extra.app.baseRoute) || "";
    setPathname($pathname);
    if (pathname === baseRoute.current) {
      return;
    }
    var targetUrl = "".concat($pathname).concat($search);
    navigate(targetUrl, {
      replace: true
    });
    if (!isTabsMode) {
      return;
    }
    addTab({
      path: $pathname,
      search: $search
    });
  };
  var handleMonitorConfig = function handleMonitorConfig(info) {
    var _URL;
    if (initSentryConfig) {
      return;
    }
    setInitSentryConfig(true);
    var url = info.app.url;
    if (url.startsWith("//")) {
      url = "http:".concat(url);
    }
    var host = (_URL = new URL(url)) === null || _URL === void 0 ? void 0 : _URL.host;
    var hostCacheKey = "@@monitorConfig_".concat(host);
    var disPatchObj = _defineProperty({}, hostCacheKey, monitorConfig === null || monitorConfig === void 0 ? void 0 : monitorConfig.options);
    // 向主应用注册自身sentry配置
    window.microApp.dispatch(disPatchObj);
    setErrorCaptureException(function (error, errorInfo) {
      var _info$parentMonitor;
      info === null || info === void 0 || (_info$parentMonitor = info.parentMonitor) === null || _info$parentMonitor === void 0 || _info$parentMonitor.captureException(error, errorInfo);
    });
  };
  useEffect(function () {
    // 子应用数据监听
    window.microApp.addDataListener(function (msg) {
      var _data$extra;
      var data = msg.data;
      switch (msg.event) {
        case "pathnameChanged":
          if (((_data$extra = data.extra) === null || _data$extra === void 0 ? void 0 : _data$extra.keepAlive) !== undefined) {
            var _data$extra2;
            setKeepAlive((_data$extra2 = data.extra) === null || _data$extra2 === void 0 ? void 0 : _data$extra2.keepAlive);
          }
          checkPathname(data);
          if (data.extra) {
            handleMonitorConfig(data.extra);
          }
          break;
        case "tabRemoved":
          removeTabs(msg.data.pathnames);
          break;
        case "refreshPath":
          setRefreshPath(msg.data.path);
          break;
        default:
          break;
      }
    }, true);
    return function () {
      return window.microApp.clearDataListener();
    };
  }, []);

  // 扁平化路由
  useEffect(function () {
    setFlatPathRoutes(clientRoutes);
  }, [clientRoutes]);
  useEffect(function () {
    initTabEvent();
  }, []);
  var proLayoutProps = useProLayoutProps(props, props.proLayoutConfig);
  var routesInit = ((_Object$keys = Object.keys(flatPathRoutes)) === null || _Object$keys === void 0 ? void 0 : _Object$keys.length) > 0;
  if (!routesInit) {
    return null;
  }
  var composeTheme = {
    prefixCls: prefixCls,
    proLayout: ((_props$proLayoutConfi = props.proLayoutConfig) === null || _props$proLayoutConfi === void 0 ? void 0 : _props$proLayoutConfi.token) || {},
    token: themeToken
  };
  return /*#__PURE__*/_jsx("div", {
    ref: subAppRef,
    children: /*#__PURE__*/_jsx(ConfigProvider, {
      getPopupContainer: function getPopupContainer() {
        // tabs下子应用默认挂载自己的窗口中
        if (isTabsMode) {
          return subAppRef.current || document.body;
        }
        return document.body;
      },
      children: /*#__PURE__*/_jsx(ProConfigProvider, {
        token: composeTheme.proLayout,
        children: /*#__PURE__*/_jsx(ProLayout, _objectSpread(_objectSpread({
          pure: true
        }, proLayoutProps), {}, {
          children: /*#__PURE__*/_jsxs(ThemeProvider, {
            theme: composeTheme,
            children: [/*#__PURE__*/_jsx(GlobalStyle, {}), keepAlive ? /*#__PURE__*/_jsx(PanesContainer, {
              appData: appData,
              layoutConfig: layoutConfig
            }) : /*#__PURE__*/_jsx(SingleOutlet, {
              appData: appData,
              layoutConfig: layoutConfig
            })]
          })
        }))
      })
    })
  });
};
export default SubLayout;